import React from 'react';
import { motion } from 'framer-motion';
import { Handshake, Puzzle, Rocket, Target, Zap } from 'lucide-react';
import { useLanguage } from '../../i18n';

const coreValues = [
    { icon: Zap, text: 'Hiệu suất & bảo mật thực sự' },
    { icon: Puzzle, text: 'Giải pháp có thể mở rộng' },
    { icon: Target, text: 'Tập trung trải nghiệm người dùng' },
    { icon: Handshake, text: 'Minh bạch & đồng hành dài hạn' },
    { icon: Rocket, text: 'Tối ưu tăng trưởng chuyển đổi' }
];

const About = () => {
    const { language, t } = useLanguage();
    const values = language === 'vi' ? coreValues : [
        { icon: Zap, text: 'Real performance and security' }, { icon: Puzzle, text: 'Scalable solutions' }, { icon: Target, text: 'User experience first' }, { icon: Handshake, text: 'Transparency and long-term partnership' }, { icon: Rocket, text: 'Conversion-focused growth' }
    ];
    return (
        <section className="pt-32 pb-24 bg-gray-50 min-h-screen" id="about">
            <div className="container mx-auto px-4 max-w-5xl">
                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.6 }}
                    className="mb-12"
                >
                    <h1 className="text-4xl font-extrabold mb-4 bg-gradient-to-r from-primary-600 to-primary-400 bg-clip-text text-transparent">
                        {t('about')} DevenflyWeb
                    </h1>
                    <p className="text-lg text-gray-600 leading-relaxed">
                        {language === 'vi' ? 'Chúng tôi giúp doanh nghiệp xây dựng, tối ưu và mở rộng hệ thống WordPress hiệu suất cao. Tập trung vào tốc độ, bảo mật, khả năng mở rộng và trải nghiệm người dùng.' : 'We help businesses build, optimize, and scale high-performance WordPress systems, with a focus on speed, security, scalability, and user experience.'}
                    </p>
                </motion.div>

                <div className="grid md:grid-cols-2 gap-10 mb-16">
                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6 }}
                        className="bg-white rounded-xl shadow-sm p-8 border border-gray-100"
                    >
                        <h2 className="text-2xl font-semibold mb-4">{language === 'vi' ? 'Sứ mệnh' : 'Mission'}</h2>
                        <p className="text-gray-600 leading-relaxed">
                            {language === 'vi' ? 'Tăng trưởng giá trị số cho doanh nghiệp bằng nền tảng website linh hoạt, dễ mở rộng, tích hợp sâu với hệ thống marketing và bán hàng.' : 'Grow digital value with a flexible, scalable website platform deeply connected to marketing and sales systems.'}
                        </p>
                    </motion.div>

                    <motion.div
                        initial={{ opacity: 0, y: 20 }}
                        whileInView={{ opacity: 1, y: 0 }}
                        viewport={{ once: true }}
                        transition={{ duration: 0.6, delay: 0.1 }}
                        className="bg-white rounded-xl shadow-sm p-8 border border-gray-100"
                    >
                        <h2 className="text-2xl font-semibold mb-4">{language === 'vi' ? 'Giá trị cốt lõi' : 'Core values'}</h2>
                        <ul className="space-y-3 text-gray-600">
                            {values.map(({ icon: Icon, text }) => (
                                <li key={text} className="flex items-center gap-3">
                                    <Icon size={18} strokeWidth={2} className="shrink-0 text-primary-600" aria-hidden="true" />
                                    <span>{text}</span>
                                </li>
                            ))}
                        </ul>
                    </motion.div>
                </div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="bg-white rounded-xl shadow-sm p-8 border border-gray-100 mb-16"
                >
                    <h2 className="text-2xl font-semibold mb-6">{language === 'vi' ? 'Điểm khác biệt' : 'What makes us different'}</h2>
                    <div className="grid md:grid-cols-3 gap-8">
                        {[
                            ...(language === 'vi' ? [{ title: 'Quy trình rõ ràng', desc: 'Giai đoạn chuẩn xác: phân tích → thiết kế → phát triển → tối ưu → triển khai.' }, { title: 'Chất lượng mã nguồn', desc: 'Tiêu chuẩn performance, SEO, Core Web Vitals và security hardening.' }, { title: 'Hỗ trợ liên tục', desc: 'Giám sát uptime, xử lý sự cố, tối ưu mở rộng dài hạn.' }] : [{ title: 'A clear process', desc: 'A precise path: discovery → design → build → optimize → launch.' }, { title: 'Quality code', desc: 'Performance, SEO, Core Web Vitals, and security hardening standards.' }, { title: 'Ongoing support', desc: 'Uptime monitoring, incident response, and long-term optimization.' }])
                        ].map((item, i) => (
                            <div key={i} className="space-y-2">
                                <h3 className="font-semibold text-primary-600">{item.title}</h3>
                                <p className="text-gray-600 text-sm leading-relaxed">{item.desc}</p>
                            </div>
                        ))}
                    </div>
                </motion.div>

                <motion.div
                    initial={{ opacity: 0, y: 20 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true }}
                    transition={{ duration: 0.6 }}
                    className="text-center"
                >
                    <h2 className="text-2xl font-semibold mb-4">{language === 'vi' ? 'Sẵn sàng đồng hành?' : 'Ready to work together?'}</h2>
                    <p className="text-gray-600 mb-6">{language === 'vi' ? 'Hãy bắt đầu bằng một cuộc trao đổi nhanh về nhu cầu của bạn.' : 'Start with a quick conversation about your needs.'}</p>
                    <a href="/" onClick={(e) => { e.preventDefault(); const el = document.getElementById('contact'); if (el) el.scrollIntoView({ behavior: 'smooth' }); }} className="inline-block bg-primary-600 hover:bg-primary-700 text-white px-6 py-3 rounded-lg font-medium transition-colors">
                        {t('contact')}
                    </a>
                </motion.div>
            </div>
        </section>
    );
};

export default About;
